import { shuffle } from '../utils'

export type Difficulty  = 'easy' | 'medium' | 'hard'
export type AttentionMode = 'words' | 'numbers'
export type ConditionType =
  | 'length_gt' | 'starts_with' | 'ends_with' | 'category'
  | 'is_odd' | 'is_even' | 'gt' | 'lt' | 'digit_sum_gt' | 'digit_count_eq' | 'digit_count_lt' | 'digit_count_gt' | 'multiple_of'
export type MarkType    = 'circle' | 'underline' | 'strikethrough'

export interface AttentionCondition {
  type:       ConditionType
  mark:       MarkType
  param:      string | number  // letter, min-length threshold, or theme slug
  labelKey:   string           // template key, filled by component
  count:      number           // number of words assigned to this condition
  themeLabel?: string          // display name for category conditions
}

export interface AttentionWord {
  id:       number
  text:     string
  mark:     'none' | MarkType
  condIdx:  number | null  // index into conditions[], null = distractor
}

export interface AttentionRound {
  words:      AttentionWord[]
  conditions: AttentionCondition[]
}

// ── Difficulty config ─────────────────────────────────────────────────────────

export const DIFFICULTY_CONFIG: Record<Difficulty, { wordCount: number; conditionCount: number }> = {
  easy:   { wordCount: 20, conditionCount: 1 },
  medium: { wordCount: 40, conditionCount: 2 },
  hard:   { wordCount: 60, conditionCount: 3 },
}

export const MARKS_FOR_DIFFICULTY: Record<Difficulty, MarkType[]> = {
  easy:   ['circle'],
  medium: ['circle', 'underline'],
  hard:   ['circle', 'underline', 'strikethrough'],
}

export const NUMBER_DIFFICULTY_CONFIG: Record<Difficulty, {
  numberCount: number
  range: [number, number]
  conditionPool: Exclude<ConditionType, 'length_gt' | 'starts_with' | 'ends_with' | 'category'>[]
}> = {
  easy:   { numberCount: 20, range: [1, 99],   conditionPool: ['is_odd', 'is_even', 'gt', 'lt'] },
  medium: { numberCount: 40, range: [1, 200],  conditionPool: ['is_odd', 'is_even', 'gt', 'lt', 'digit_sum_gt', 'digit_count_eq', 'digit_count_lt', 'digit_count_gt'] },
  hard:   { numberCount: 60, range: [10, 999], conditionPool: ['is_odd', 'is_even', 'gt', 'lt', 'digit_sum_gt', 'digit_count_eq', 'digit_count_lt', 'digit_count_gt', 'multiple_of'] },
}

const MIN_WORDS_PER_CONDITION = 3
const MAX_WORDS_PER_CONDITION = 7

// ── Classic Spanish word list (200 words, varied length / initials / endings) ─

export const CLASSIC_WORDS_ES: string[] = [
  // Short (3-4 chars)
  'SOL', 'MAR', 'PAN', 'LUZ', 'FIN', 'ORO', 'CAL', 'TÉ', 'MES', 'RED',
  'VOZ', 'PAZ', 'GEL', 'FEZ', 'ROL', 'CAZ', 'PEZ', 'VEZ', 'DOS', 'RAZ',
  'AMOR', 'AIRE', 'AGUA', 'GATO', 'ÁRBOL', 'LUNA', 'MESA', 'SILLA', 'CASA', 'LIBRO',
  'FLOR', 'NIÑO', 'PATO', 'VINO', 'COPA', 'ROPA', 'BOCA', 'ZONA', 'CARA', 'IDEA',
  // Medium (5-7 chars)
  'CAMPO', 'NOCHE', 'TARDE', 'CIELO', 'VIENTO', 'PERRO', 'BANCO', 'VERDE', 'BLANCO', 'NEGRO',
  'PLAYA', 'MONTE', 'BOSQUE', 'PIEDRA', 'FUENTE', 'PUERTA', 'VENTANA', 'CAMINO', 'JARDÍN', 'CIUDAD',
  'LLAVES', 'BARCO', 'COCHE', 'TREN', 'AVIÓN', 'COCINA', 'BAÑO', 'CAMA', 'SUELO', 'TECHO',
  'PAPEL', 'LÁPIZ', 'RELOJ', 'BOLSO', 'CAJÓN', 'VASO', 'PLATO', 'CUCHARA', 'TENEDOR', 'CUCHILLO',
  'NIETA', 'ABUELO', 'VECINO', 'AMIGO', 'MÉDICO', 'ENFERMERA', 'FARMACIA', 'MERCADO', 'IGLESIA', 'COLEGIO',
  // Longer (8+ chars)
  'MARIPOSA', 'ESTACIÓN', 'INVIERNO', 'PRIMAVERA', 'OTOÑO', 'VERANO', 'MONTAÑA', 'HORIZONTE', 'AVENTURA', 'RECUERDO',
  'FAMILIA', 'AMISTAD', 'ALEGRÍA', 'TRISTEZA', 'ESPERANZA', 'VOLUNTAD', 'PACIENCIA', 'FORTALEZA', 'SABIDURÍA', 'HUMILDAD',
  'DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO', 'ENERO', 'FEBRERO', 'MARZO',
  'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE', 'NAVIDAD',
  'ZAPATO', 'CAMISA', 'PANTALÓN', 'ABRIGO', 'BUFANDA', 'GUANTE', 'SOMBRERO', 'CINTURÓN', 'CALCETÍN', 'VESTIDO',
  // More variety
  'PIANO', 'GUITARRA', 'VIOLÍN', 'TROMPETA', 'FLAUTA', 'BATERÍA', 'ARMONÍA', 'MELODÍA', 'CANCIÓN', 'BAILE',
  'PINTURA', 'DIBUJO', 'ESCULTURA', 'FOTOGRAFÍA', 'TEATRO', 'CINEMA', 'NOVELA', 'POEMA', 'CUENTO', 'HISTORIA',
  'CABALLO', 'VACA', 'OVEJA', 'CERDO', 'GALLINA', 'CONEJO', 'PALOMA', 'CIGÜEÑA', 'GOLONDRINA', 'MURCIÉLAGO',
  'TOMATE', 'PATATA', 'ZANAHORIA', 'CEBOLLA', 'LECHUGA', 'PEPINO', 'PIMIENTO', 'CALABAZA', 'BERENJENA', 'ESPINACA',
  'MANZANA', 'NARANJA', 'LIMÓN', 'UVA', 'FRESA', 'MELÓN', 'SANDÍA', 'PERA', 'MELOCOTÓN', 'CEREZA',
]

export const CLASSIC_WORDS_EN: string[] = [
  'SUN', 'SEA', 'BED', 'CAR', 'CAT', 'DOG', 'HAT', 'MAP', 'JAR', 'KEY',
  'CUP', 'BOX', 'NET', 'FOX', 'LOG', 'HOP', 'MUG', 'PEN', 'RUG', 'TIN',
  'LOVE', 'MOON', 'TREE', 'BOOK', 'ROSE', 'BIRD', 'STAR', 'FIRE', 'RAIN', 'SNOW',
  'DOOR', 'WALL', 'ROOF', 'LAMP', 'FORK', 'BOWL', 'SPOON', 'COAT', 'BOOT', 'RING',
  'CLOUD', 'RIVER', 'FIELD', 'STONE', 'BREAD', 'KNIFE', 'CHAIR', 'TABLE', 'CLOCK', 'GLASS',
  'FLOWER', 'GARDEN', 'SUMMER', 'WINTER', 'AUTUMN', 'SPRING', 'CANDLE', 'MIRROR', 'PILLOW', 'CARPET',
  'MORNING', 'EVENING', 'KITCHEN', 'BEDROOM', 'VILLAGE', 'STATION', 'LIBRARY', 'TEACHER', 'STUDENT', 'SERVANT',
  'MOUNTAIN', 'SUNSHINE', 'DARKNESS', 'PATIENCE', 'KINDNESS', 'STRENGTH', 'FREEDOM', 'MEMORY', 'JOURNEY', 'ADVENTURE',
  'APPLE', 'ORANGE', 'LEMON', 'GRAPE', 'MELON', 'CHERRY', 'PEACH', 'PLUM', 'MANGO', 'BANANA',
  'TOMATO', 'CARROT', 'ONION', 'GARLIC', 'POTATO', 'PEPPER', 'LETTUCE', 'SPINACH', 'CUCUMBER', 'PUMPKIN',
  'HORSE', 'SHEEP', 'COW', 'PIG', 'HEN', 'RABBIT', 'PIGEON', 'SPARROW', 'EAGLE', 'BUTTERFLY',
  'PIANO', 'GUITAR', 'VIOLIN', 'TRUMPET', 'FLUTE', 'DRUM', 'MELODY', 'HARMONY', 'SONG', 'DANCE',
  'PAINTING', 'DRAWING', 'SCULPTURE', 'PHOTOGRAPH', 'THEATRE', 'CINEMA', 'NOVEL', 'POEM', 'STORY', 'HISTORY',
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY', 'JANUARY', 'FEBRUARY', 'MARCH',
  'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER', 'CHRISTMAS',
  'JACKET', 'SHIRT', 'TROUSERS', 'DRESS', 'SKIRT', 'SCARF', 'GLOVE', 'SHOE', 'SOCK', 'BELT',
  'FAMILY', 'FRIEND', 'DOCTOR', 'NURSE', 'MARKET', 'CHURCH', 'SCHOOL', 'HOSPITAL', 'PHARMACY', 'NEIGHBOUR',
  'WONDER', 'CANOE', 'HARBOR', 'FOREST', 'DESERT', 'ISLAND', 'PALACE', 'CASTLE', 'BRIDGE', 'TUNNEL',
  'SILVER', 'GOLDEN', 'PURPLE', 'YELLOW', 'CRIMSON', 'EMERALD', 'SAPPHIRE', 'DIAMOND', 'CRYSTAL', 'MARBLE',
  'THUNDER', 'RAINBOW', 'HORIZON', 'TWILIGHT', 'BLOSSOM', 'HARVEST', 'FESTIVE', 'WELCOME', 'COMFORT', 'LAUGHTER',
]

// ── Normalize (strip accents, uppercase) for condition matching ───────────────

function normalize(s: string): string {
  return s.toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

function digitSum(n: number): number {
  return String(n).split('').reduce((s, d) => s + Number(d), 0)
}

function digitCount(n: number): number {
  return String(n).replace('-', '').length
}

function wordMatchesCondition(word: string, cond: AttentionCondition, themeWordSet?: Set<string>): boolean {
  const n = normalize(word)
  switch (cond.type) {
    case 'length_gt':   return n.length > (cond.param as number)
    case 'starts_with': return n.startsWith(cond.param as string)
    case 'ends_with':   return n.endsWith(cond.param as string)
    case 'category':    return themeWordSet ? themeWordSet.has(word) : false
    case 'is_odd':      { const num = Number(word); return !isNaN(num) && num % 2 !== 0 }
    case 'is_even':     { const num = Number(word); return !isNaN(num) && num % 2 === 0 }
    case 'gt':          { const num = Number(word); return !isNaN(num) && num > (cond.param as number) }
    case 'lt':          { const num = Number(word); return !isNaN(num) && num < (cond.param as number) }
    case 'digit_sum_gt':{ const num = Number(word); return !isNaN(num) && digitSum(num) > (cond.param as number) }
    case 'digit_count_eq':{ const num = Number(word); return !isNaN(num) && digitCount(num) === (cond.param as number) }
    case 'digit_count_lt':{ const num = Number(word); return !isNaN(num) && digitCount(num) < (cond.param as number) }
    case 'digit_count_gt':{ const num = Number(word); return !isNaN(num) && digitCount(num) > (cond.param as number) }
    case 'multiple_of': { const num = Number(word); return !isNaN(num) && num % (cond.param as number) === 0 }
    default:            return false
  }
}

// ── Round builder ─────────────────────────────────────────────────────────────

interface ThemePool {
  slug:  string
  label: string
  words: string[]
}

interface CondSpec {
  type:       ConditionType
  param:      string | number
  themeSlug?: string
  themeLabel?: string
}

function specMatches(word: string, spec: CondSpec, themeWordSets: Map<string, Set<string>>): boolean {
  const n = normalize(word)
  switch (spec.type) {
    case 'length_gt':   return n.length > (spec.param as number)
    case 'starts_with': return n.startsWith(spec.param as string)
    case 'ends_with':   return n.endsWith(spec.param as string)
    case 'category':    return themeWordSets.get(spec.themeSlug!)?.has(word) ?? false
    default:            return false
  }
}

export function buildRound(
  difficulty: Difficulty,
  language:   'es' | 'en',
  themePools: ThemePool[],
  classicWords?: { es: string[]; en: string[] },
): AttentionRound {
  const { wordCount, conditionCount } = DIFFICULTY_CONFIG[difficulty]
  const classicWordList = classicWords
    ? (language === 'es' ? classicWords.es : classicWords.en)
    : (language === 'es' ? CLASSIC_WORDS_ES : CLASSIC_WORDS_EN)

  const allThemeWords = themePools.flatMap(t => t.words)
  const fullPool = shuffle([...new Set([...classicWordList, ...allThemeWords])])

  const themeWordSets = new Map<string, Set<string>>(
    themePools.map(t => [t.slug, new Set(t.words)]),
  )

  // ── Phase 1: generate all candidate specs ──────────────────────────────────
  const candidateSpecs: CondSpec[] = []
  for (const threshold of [3, 4, 5, 6, 7]) {
    candidateSpecs.push({ type: 'length_gt', param: threshold })
  }
  for (const letter of 'ABCDEFGHIJKLMNOPRSTV'.split('')) {
    candidateSpecs.push({ type: 'starts_with', param: letter })
  }
  for (const letter of 'AEINOSR'.split('')) {
    candidateSpecs.push({ type: 'ends_with', param: letter })
  }
  for (const theme of themePools) {
    candidateSpecs.push({ type: 'category', param: theme.slug, themeSlug: theme.slug, themeLabel: theme.label })
  }

  // ── Phase 2: greedily pick specs with enough EXCLUSIVE matching words ───────
  // "exclusive" = matches this spec but no other selected spec
  const selectedSpecs: CondSpec[] = []
  for (const spec of shuffle(candidateSpecs)) {
    if (selectedSpecs.length >= conditionCount) break
    const tentative = [...selectedSpecs, spec]
    const allHaveEnough = tentative.every(s => {
      const exclusive = fullPool.filter(w =>
        specMatches(w, s, themeWordSets) &&
        tentative.every(other => other === s || !specMatches(w, other, themeWordSets)),
      )
      return exclusive.length >= MIN_WORDS_PER_CONDITION
    })
    if (allHaveEnough) selectedSpecs.push(spec)
  }

  // ── Phase 3: assign marks in fixed order (circle → underline → strikethrough)
  const availableMarks = MARKS_FOR_DIFFICULTY[difficulty]

  const conditions: AttentionCondition[] = []
  const usedWords = new Set<string>()
  const wordAssignments: Array<{ word: string; condIdx: number }> = []

  for (let i = 0; i < selectedSpecs.length; i++) {
    const spec = selectedSpecs[i]
    const mark = availableMarks[i]

    const exclusiveWords = fullPool.filter(w =>
      !usedWords.has(w) &&
      specMatches(w, spec, themeWordSets) &&
      selectedSpecs.every((other, j) => j === i || !specMatches(w, other, themeWordSets)),
    )
    const chosen = shuffle(exclusiveWords).slice(0, MAX_WORDS_PER_CONDITION)
    chosen.forEach(w => usedWords.add(w))
    wordAssignments.push(...chosen.map(w => ({ word: w, condIdx: i })))
    conditions.push({
      type:       spec.type,
      mark,
      param:      spec.param,
      labelKey:   spec.type,
      count:      chosen.length,
      themeLabel: spec.themeLabel,
    })
  }

  // ── Phase 4: distractors — match no selected spec ──────────────────────────
  const distractorPool = fullPool.filter(w =>
    !usedWords.has(w) &&
    !selectedSpecs.some(s => specMatches(w, s, themeWordSets)),
  )
  const distractorCount = Math.max(0, wordCount - wordAssignments.length)
  const distractors     = shuffle(distractorPool).slice(0, distractorCount)

  // Build AttentionWord list
  const wordsOut: AttentionWord[] = [
    ...wordAssignments.map((a, i) => ({
      id:      i,
      text:    a.word,
      mark:    'none' as const,
      condIdx: a.condIdx,
    })),
    ...distractors.map((w, i) => ({
      id:      wordAssignments.length + i,
      text:    w,
      mark:    'none' as const,
      condIdx: null,
    })),
  ]

  return {
    words:      shuffle(wordsOut),
    conditions,
  }
}

// ── Tap cycle ──────────────────────────────────────────────────────────────────

export function nextMark(current: AttentionWord['mark'], availableMarks: MarkType[]): AttentionWord['mark'] {
  const cycle: Array<AttentionWord['mark']> = ['none', ...availableMarks]
  const idx = cycle.indexOf(current)
  if (idx === -1) return 'none'
  return cycle[(idx + 1) % cycle.length]
}

// ── Check result ──────────────────────────────────────────────────────────────

export interface CheckResult {
  correct:   number
  total:     number  // words that matter (condition words + any incorrectly marked distractors)
  wrongIds:  number[]
}

export function checkAnswers(
  words:      AttentionWord[],
  conditions: AttentionCondition[],
  themePools: { slug: string; words: string[] }[] = [],
): CheckResult {
  const themeWordSets = new Map<string, Set<string>>(
    themePools.map(t => [t.slug, new Set(t.words)]),
  )

  const wrongIds: number[] = []
  let correct = 0
  let total   = 0

  for (const word of words) {
    // Find which condition this word's text actually satisfies (criteria-based, not pre-assignment)
    const matchingCond = conditions.find(c =>
      wordMatchesCondition(word.text, c, themeWordSets.get(c.param as string)),
    )
    const expectedMark = matchingCond ? matchingCond.mark : 'none'

    total++
    if (word.mark === expectedMark) {
      correct++
    } else {
      wrongIds.push(word.id)
    }
  }

  return { correct, total, wrongIds }
}

// ── Number round builder ──────────────────────────────────────────────────────

function specMatchesNumber(n: number, spec: NumberCondSpec): boolean {
  switch (spec.type) {
    case 'is_odd':          return n % 2 !== 0
    case 'is_even':         return n % 2 === 0
    case 'gt':              return n > (spec.param as number)
    case 'lt':              return n < (spec.param as number)
    case 'digit_sum_gt':    return digitSum(n) > (spec.param as number)
    case 'digit_count_eq':  return digitCount(n) === (spec.param as number)
    case 'digit_count_lt':  return digitCount(n) < (spec.param as number)
    case 'digit_count_gt':  return digitCount(n) > (spec.param as number)
    case 'multiple_of':     return n % (spec.param as number) === 0
    default:                return false
  }
}

interface NumberCondSpec {
  type: ConditionType
  param: number
}

const DIGIT_SUM_THRESHOLDS: Record<Difficulty, number[]> = {
  easy:   [],
  medium: [8, 9, 10],
  hard:   [10, 11, 12],
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function buildNumberRound(difficulty: Difficulty): AttentionRound {
  const numConfig = NUMBER_DIFFICULTY_CONFIG[difficulty]
  const wordConfig = DIFFICULTY_CONFIG[difficulty]
  const { numberCount, range, conditionPool } = numConfig
  const conditionCount = wordConfig.conditionCount

  // Generate unique random numbers in range
  const allNumbers = new Set<number>()
  while (allNumbers.size < numberCount) {
    allNumbers.add(randomInt(range[0], range[1]))
  }
  const numberPool = Array.from(allNumbers)

  // Build candidate specs
  const candidateSpecs: NumberCondSpec[] = []
  const numberPoolSet = new Set(numberPool)

  // gt/lt: threshold must not be in numberPool — if the boundary value
  // appeared in the grid as a distractor, players would tap it under
  // "Greater than X" and get a wrong flash (25 looks like it should match
  // "Greater than 25"). By skipping pool values as thresholds we avoid the
  // ambiguity without shrinking the grid.
  const rangeSize = range[1] - range[0]
  const gtMin = range[0] + Math.floor(rangeSize / 3)
  const gtMax = range[0] + Math.floor((2 * rangeSize) / 3)
  for (let x = gtMin; x <= gtMax; x += Math.max(1, Math.floor(rangeSize / 6))) {
    if (!numberPoolSet.has(x)) candidateSpecs.push({ type: 'gt', param: x })
  }

  const ltMin = range[0] + Math.floor(rangeSize / 3)
  const ltMax = range[0] + Math.floor((2 * rangeSize) / 3)
  for (let x = ltMin; x <= ltMax; x += Math.max(1, Math.floor(rangeSize / 6))) {
    if (!numberPoolSet.has(x)) candidateSpecs.push({ type: 'lt', param: x })
  }

  // digit_sum_gt
  for (const y of DIGIT_SUM_THRESHOLDS[difficulty]) {
    candidateSpecs.push({ type: 'digit_sum_gt', param: y })
  }

  // digit_count_eq: derive valid digit lengths present in the range
  const presentDigitLengths = new Set<number>()
  for (const n of numberPool) {
    presentDigitLengths.add(digitCount(n))
  }
  for (const n of presentDigitLengths) {
    candidateSpecs.push({ type: 'digit_count_eq', param: n })
    candidateSpecs.push({ type: 'digit_count_lt', param: n })
    candidateSpecs.push({ type: 'digit_count_gt', param: n })
  }

  // multiple_of — only odd primes {3, 5, 7}
  for (const m of [3, 5, 7]) {
    candidateSpecs.push({ type: 'multiple_of', param: m })
  }

  // Filter candidates by conditionPool
  const filteredCandidates = candidateSpecs.filter(s =>
    (conditionPool as string[]).includes(s.type),
  )

  // Greedily pick specs with enough exclusive matching numbers
  // Never pick both is_odd and is_even
  const selectedSpecs: NumberCondSpec[] = []
  const hasParity = (specs: NumberCondSpec[]) =>
    specs.some(s => s.type === 'is_odd' || s.type === 'is_even')

  // Add parity spec first if conditionCount >= 1
  const parityCandidates: NumberCondSpec[] = ([
    { type: 'is_odd', param: 0 },
    { type: 'is_even', param: 0 },
  ] as NumberCondSpec[]).filter(s => (conditionPool as string[]).includes(s.type))

  for (const spec of shuffle([...filteredCandidates, ...parityCandidates])) {
    if (selectedSpecs.length >= conditionCount) break

    // Skip if would add second parity
    if ((spec.type === 'is_odd' || spec.type === 'is_even') && hasParity(selectedSpecs)) continue

    const tentative = [...selectedSpecs, spec]
    const allHaveEnough = tentative.every(s => {
      const exclusive = numberPool.filter(n =>
        specMatchesNumber(n, s) &&
        tentative.every(other => other === s || !specMatchesNumber(n, other)),
      )
      return exclusive.length >= MIN_WORDS_PER_CONDITION
    })
    const safeInRange: number[] = []
    for (let n = range[0]; n <= range[1]; n++) {
      if (!tentative.some(s => specMatchesNumber(n, s))) safeInRange.push(n)
    }
    const neededSafe = numberCount - tentative.length * MAX_WORDS_PER_CONDITION
    if (allHaveEnough && safeInRange.length >= Math.max(0, neededSafe)) {
      selectedSpecs.push(spec)
    }
  }

  // If we couldn't get enough specs, fall back to at least 1
  if (selectedSpecs.length === 0 && conditionPool.length > 0) {
    const fallback = conditionPool.find(t => t === 'gt' || t === 'lt') ??
                     conditionPool.find(t => t === 'is_even' || t === 'is_odd') ??
                     'is_even'
    const fallbackParam = fallback === 'gt' || fallback === 'lt'
      ? Math.floor((range[0] + range[1]) / 2)
      : 0
    selectedSpecs.push({ type: fallback, param: fallbackParam })
  }

  // Assign marks
  const availableMarks = MARKS_FOR_DIFFICULTY[difficulty]

  const conditions: AttentionCondition[] = []
  const usedNumbers = new Set<number>()
  const numberAssignments: Array<{ num: number; condIdx: number }> = []

  for (let i = 0; i < selectedSpecs.length; i++) {
    const spec = selectedSpecs[i]
    const mark = availableMarks[i]

    const exclusiveNumbers = numberPool.filter(n =>
      !usedNumbers.has(n) &&
      specMatchesNumber(n, spec) &&
      selectedSpecs.every((other, j) => j === i || !specMatchesNumber(n, other)),
    )
    const chosen = shuffle(exclusiveNumbers).slice(0, MAX_WORDS_PER_CONDITION)
    chosen.forEach(n => usedNumbers.add(n))
    numberAssignments.push(...chosen.map(n => ({ num: n, condIdx: i })))
    conditions.push({
      type:     spec.type,
      mark,
      param:    spec.param,
      labelKey: spec.type,
      count:    chosen.length,
    })
  }

  // Distractors — match no selected spec
  const distractorPool = numberPool.filter(n =>
    !usedNumbers.has(n) &&
    !selectedSpecs.some(s => specMatchesNumber(n, s)),
  )
  const distractorCount = Math.max(0, numberCount - numberAssignments.length)
  const distractors = shuffle(distractorPool).slice(0, distractorCount)

  const wordsOut: AttentionWord[] = [
    ...numberAssignments.map((a, i) => ({
      id:      i,
      text:    String(a.num),
      mark:    'none' as const,
      condIdx: a.condIdx,
    })),
    ...distractors.map((n, i) => ({
      id:      numberAssignments.length + i,
      text:    String(n),
      mark:    'none' as const,
      condIdx: null,
    })),
  ]

  // Pad to exactly numberCount with fresh numbers that match no selected spec.
  // Build the candidate list deterministically to avoid an infinite rejection loop.
  if (wordsOut.length < numberCount) {
    const paddingCandidates: number[] = []
    for (let n = range[0]; n <= range[1]; n++) {
      if (!allNumbers.has(n) && !selectedSpecs.some(s => specMatchesNumber(n, s))) {
        paddingCandidates.push(n)
      }
    }
    const shuffledPadding = shuffle(paddingCandidates)
    let pIdx = 0
    while (wordsOut.length < numberCount && pIdx < shuffledPadding.length) {
      const n = shuffledPadding[pIdx++]
      wordsOut.push({ id: wordsOut.length, text: String(n), mark: 'none', condIdx: null })
    }
  }

  // Last resort: spec-matching pool numbers that weren't assigned due to MAX_WORDS_PER_CONDITION cap.
  // Add them as correct answers for their condition so the game stays consistent.
  if (wordsOut.length < numberCount) {
    const usedTexts = new Set(wordsOut.map(w => w.text))
    for (const n of shuffle([...numberPool])) {
      if (wordsOut.length >= numberCount) break
      if (usedTexts.has(String(n))) continue
      const matchIdx = selectedSpecs.findIndex(s => specMatchesNumber(n, s))
      if (matchIdx >= 0) {
        wordsOut.push({ id: wordsOut.length, text: String(n), mark: 'none', condIdx: matchIdx })
        conditions[matchIdx] = { ...conditions[matchIdx], count: conditions[matchIdx].count + 1 }
        usedTexts.add(String(n))
      }
    }
  }

  return {
    words:      shuffle(wordsOut),
    conditions,
  }
}
